import { cn } from "@/utils";
import { useId, type ReactNode } from "react";

/**
 * Escolha única entre duas ou três opções, todas à vista.
 *
 * É o que o inventário sempre mandou usar no lugar do `Select` quando a lista
 * é curta ("duas ou três opções — use rádio"), e que não existia: as telas
 * resolviam com fileiras de `<button>` que não andavam pela seta, não se
 * anunciavam como escolha e não entravam num formulário.
 *
 * Por dentro são **rádios nativos**, escondidos mas presentes. É deles que vêm
 * de graça o grupo (mesmo `name`), a navegação por setas, o "um só marcado" e
 * o envio junto do formulário. O que desenhamos é só a moldura.
 */

export type RadioOption = {
  value: string;
  label: string;
  /** Segunda linha, só em `orientation="stack"`. Diga a consequência. */
  description?: string;
  disabled?: boolean;
};

export interface RadioGroupProps {
  /** Vira a legenda do grupo. Sem ela, o leitor de tela lê opções soltas. */
  label: string;
  hideLabel?: boolean;
  options: RadioOption[];
  value: string;
  onValueChange: (value: string) => void;
  /**
   * `stack` (padrão): uma por linha, com espaço para descrição.
   * `inline`: lado a lado, para rótulos curtos que cabem na linha.
   */
  orientation?: "stack" | "inline";
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /** Nome do campo num formulário HTML comum. Gerado quando omitido. */
  name?: string;
  className?: string;
}

export const RadioGroup = ({
  label,
  hideLabel,
  options,
  value,
  onValueChange,
  orientation = "stack",
  hint,
  error,
  required,
  disabled,
  name,
  className,
}: RadioGroupProps) => {
  const idBase = useId();
  const grupo = name ?? idBase;
  const hintId = `${idBase}-hint`;
  const errorId = `${idBase}-error`;

  return (
    <fieldset
      className={cn("flex min-w-0 flex-col gap-1.5", className)}
      aria-describedby={cn(hint && hintId, error && errorId) || undefined}
      aria-invalid={error ? true : undefined}
      disabled={disabled}
    >
      <legend
        className={cn(
          "font-ds-sans text-ds-small text-ds-ink-700 mb-1.5 font-medium",
          hideLabel && "sr-only"
        )}
      >
        {label}
        {required ? (
          <span className="text-ds-danger-700" aria-hidden="true">
            {" "}
            *
          </span>
        ) : null}
      </legend>

      <div
        className={cn(
          "flex gap-2",
          orientation === "stack" ? "flex-col" : "flex-row flex-wrap"
        )}
      >
        {options.map(option => {
          const marcado = option.value === value;
          return (
            <label
              key={option.value}
              className={cn(
                // `relative` prende o rádio `sr-only` (absolute) à moldura: sem
                // isso ele se posiciona pela página e, dentro de um container
                // com scroll, estica o documento e deixa um vão branco no fim.
                "rounded-ds-md ease-ds-out relative flex min-h-11 cursor-pointer items-start gap-3 border px-3 py-2.5 transition-colors duration-150 motion-reduce:transition-none",
                orientation === "inline" && "flex-1 items-center",
                marcado
                  ? "border-ds-brand-500 bg-ds-brand-50"
                  : "border-ds-border hover:bg-ds-ink-50",
                (option.disabled || disabled) &&
                  "cursor-not-allowed opacity-55 hover:bg-transparent",
                // O anel é da moldura inteira: o rádio real está escondido, e
                // sem isto o foco de teclado não apareceria em lugar nenhum.
                "has-[input:focus-visible]:ring-ds-focus has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-offset-2"
              )}
            >
              <input
                type="radio"
                name={grupo}
                value={option.value}
                checked={marcado}
                disabled={option.disabled || disabled}
                onChange={() => onValueChange(option.value)}
                className="sr-only"
              />

              {/* A bolinha desenhada. A cor sozinha não diz nada a quem não a
                  enxerga — quem carrega o estado é o `checked` do rádio real. */}
              <span
                aria-hidden="true"
                className={cn(
                  "mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border",
                  orientation === "inline" && "mt-0",
                  marcado ? "border-ds-brand-600" : "border-ds-border-strong"
                )}
              >
                {marcado ? (
                  <span className="bg-ds-brand-600 size-2.5 rounded-full" />
                ) : null}
              </span>

              <span className="flex min-w-0 flex-col">
                <span
                  className={cn(
                    "font-ds-sans text-ds-small font-medium",
                    marcado ? "text-ds-ink-900" : "text-ds-ink-700"
                  )}
                >
                  {option.label}
                </span>
                {option.description && orientation === "stack" ? (
                  <span className="font-ds-sans text-ds-caption text-ds-ink-600">
                    {option.description}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>

      {hint && !error ? (
        <p
          id={hintId}
          className="font-ds-sans text-ds-caption text-ds-ink-600 mt-1"
        >
          {hint}
        </p>
      ) : null}

      {error ? (
        <p
          id={errorId}
          role="alert"
          className="font-ds-sans text-ds-caption text-ds-danger-700 mt-1"
        >
          {error}
        </p>
      ) : null}
    </fieldset>
  );
};
